import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  FiPlus, FiEdit2, FiTrash2, FiAlertCircle, FiChevronRight, FiChevronDown,
  FiFolder, FiFileText, FiEye, FiEyeOff, FiSearch, FiX, FiCheck, FiHash,
  FiSave,
} from 'react-icons/fi';
import { FaTimesCircle } from 'react-icons/fa';
import Drawer        from 'components/common/Drawer';
import Button        from 'components/common/Button';
import ToggleSwitch  from 'components/common/ToggleSwitch';
import RichTextEditor from 'components/common/RichTextEditor';
import confirmAlert  from 'components/common/confirmAlert';
import {
  fetchUserGuides,
  createUserGuide,
  updateUserGuide,
  deleteUserGuide,
  uploadUserGuideImage,
} from 'redux/documentation/userGuideSlice';

// ── Helpers ───────────────────────────────────────────────────────────────────
const SEP = ' / ';
const parseGroup = (groupName) => {
  const idx = (groupName || '').indexOf(SEP);
  return idx >= 0
    ? { parent: groupName.slice(0, idx), sub: groupName.slice(idx + SEP.length) }
    : { parent: groupName || '', sub: '' };
};

const EMOJI_RE = /(?:[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{2300}-\u{23FF}\u{1F000}-\u{1F2FF}]|\u{FE0F}|\u{200D})/gu;
const stripEmoji = (s) => (s || '').replace(EMOJI_RE, '').replace(/\s+/g, ' ').trim();

const GROUP_PALETTE = [
  { dot: 'tw-bg-primary-500',  tint: 'tw-bg-primary-50/60',  text: 'tw-text-primary-700',  border: 'tw-border-primary-200',  countBg: 'tw-bg-primary-100 tw-text-primary-700'  },
  { dot: 'tw-bg-blue-500',    tint: 'tw-bg-blue-50/60',    text: 'tw-text-blue-700',    border: 'tw-border-blue-200',    countBg: 'tw-bg-blue-100 tw-text-blue-700'    },
  { dot: 'tw-bg-sky-500',     tint: 'tw-bg-sky-50/60',     text: 'tw-text-sky-700',     border: 'tw-border-sky-200',     countBg: 'tw-bg-sky-100 tw-text-sky-700'     },
  { dot: 'tw-bg-cyan-500',    tint: 'tw-bg-cyan-50/60',    text: 'tw-text-cyan-700',    border: 'tw-border-cyan-200',    countBg: 'tw-bg-cyan-100 tw-text-cyan-700'    },
  { dot: 'tw-bg-teal-500',    tint: 'tw-bg-teal-50/60',    text: 'tw-text-teal-700',    border: 'tw-border-teal-200',    countBg: 'tw-bg-teal-100 tw-text-teal-700'    },
  { dot: 'tw-bg-emerald-500', tint: 'tw-bg-emerald-50/60', text: 'tw-text-emerald-700', border: 'tw-border-emerald-200', countBg: 'tw-bg-emerald-100 tw-text-emerald-700' },
  { dot: 'tw-bg-violet-500',  tint: 'tw-bg-violet-50/60',  text: 'tw-text-violet-700',  border: 'tw-border-violet-200',  countBg: 'tw-bg-violet-100 tw-text-violet-700'  },
  { dot: 'tw-bg-rose-500',    tint: 'tw-bg-rose-50/60',    text: 'tw-text-rose-700',    border: 'tw-border-rose-200',    countBg: 'tw-bg-rose-100 tw-text-rose-700'    },
];

const colorForGroup = (name) => {
  const s = name || '';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return GROUP_PALETTE[Math.abs(h) % GROUP_PALETTE.length];
};

const EMPTY_FORM = {
  title: '', group_name: 'General', description: '',
  content_html: '', position: 0, published: false,
};

// ── Group combobox (dynamic, free-text + autocomplete) ────────────────────────
const GroupCombobox = ({ value, onChange, suggestions = [] }) => {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const ref      = useRef(null);
  const inputRef = useRef(null);
  const c = colorForGroup(parseGroup(value).parent);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return suggestions;
    return suggestions.filter(g => g.toLowerCase().includes(q));
  }, [suggestions, query]);

  const trimmed = query.trim();
  const canCreate = trimmed.length > 0 &&
    !suggestions.some(s => s.toLowerCase() === trimmed.toLowerCase());

  const select = (v) => {
    onChange(v);
    setOpen(false);
    setQuery('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (canCreate) select(trimmed);
      else if (filtered.length > 0) select(filtered[0]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className="tw-relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`tw-inline-flex tw-items-center tw-gap-2 tw-px-3 tw-h-8 tw-text-xs tw-font-semibold tw-rounded-xl tw-border tw-cursor-pointer tw-transition-all hover:tw-shadow-sm ${c.tint} ${c.border} ${c.text}`}
      >
        <span className={`tw-w-2 tw-h-2 tw-rounded-xl ${c.dot}`} />
        <FiFolder size={11} />
        <span>{(value || 'General').replace(' / ', ' › ')}</span>
        <FiChevronDown size={11} className={`tw-transition-transform ${open ? 'tw-rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="tw-absolute tw-top-full tw-left-0 tw-mt-1.5 tw-w-80 tw-bg-white tw-border tw-border-slate-200 tw-rounded-lg tw-shadow-xl tw-py-1.5 tw-z-50">
          <div className="tw-px-2 tw-pb-2 tw-border-b tw-border-slate-100">
            <div className="tw-relative">
              <FiSearch className="tw-absolute tw-left-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400 tw-pointer-events-none" size={12} />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search or type new..."
                className="tw-w-full tw-h-8 tw-pl-7 tw-pr-2 tw-text-xs tw-text-slate-800 tw-border tw-border-slate-200 tw-rounded-md tw-bg-slate-50 focus:tw-bg-white focus:tw-border-primary-500 focus:tw-ring-2 focus:tw-ring-primary-500/15 tw-outline-none"
              />
            </div>
            <p className="tw-text-[10px] tw-text-slate-400 tw-mt-1 tw-mb-0 tw-px-0.5">
              Use <span className="tw-font-mono tw-text-slate-600">Parent / Sub</span> for sub-groups.
            </p>
          </div>
          <div className="auto-hide-scroll tw-max-h-64 tw-overflow-y-auto">
            {filtered.length === 0 && !canCreate ? (
              <p className="tw-text-center tw-text-xs tw-text-slate-400 tw-py-4 tw-mb-0 tw-italic">
                {suggestions.length === 0 ? 'No groups yet — type to create one.' : 'No matches'}
              </p>
            ) : (
              <>
                {filtered.map(opt => {
                  const oc = colorForGroup(parseGroup(opt).parent);
                  const isActive = value === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => select(opt)}
                      className={`tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-text-xs tw-text-left tw-border-0 tw-cursor-pointer tw-transition-colors hover:tw-bg-slate-100 ${isActive ? 'tw-bg-primary-50' : 'tw-bg-transparent'}`}
                    >
                      <span className={`tw-w-2 tw-h-2 tw-rounded-xl ${oc.dot} tw-shrink-0`} />
                      <span className={`tw-flex-1 tw-truncate ${isActive ? 'tw-text-primary-700 tw-font-semibold' : 'tw-text-slate-700'}`}>
                        {opt.replace(' / ', ' › ')}
                      </span>
                      {isActive && <FiCheck size={12} className="tw-text-primary-600" />}
                    </button>
                  );
                })}
                {canCreate && (
                  <button
                    type="button"
                    onClick={() => select(trimmed)}
                    className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-xs tw-text-left tw-border-0 tw-bg-transparent hover:tw-bg-emerald-50 tw-cursor-pointer tw-border-t tw-border-slate-100"
                  >
                    <div className="tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center tw-rounded-sm tw-bg-emerald-100 tw-shrink-0">
                      <FiPlus size={10} className="tw-text-emerald-700" />
                    </div>
                    <span className="tw-text-slate-500">Create new:</span>
                    <span className="tw-text-emerald-700 tw-font-bold tw-truncate">{trimmed.replace(' / ', ' › ')}</span>
                    <kbd className="tw-ml-auto tw-text-[9px] tw-font-bold tw-text-slate-400 tw-bg-slate-100 tw-border tw-border-slate-200 tw-rounded-sm tw-px-1 tw-py-0.5">↵</kbd>
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

GroupCombobox.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  suggestions: PropTypes.arrayOf(PropTypes.string),
};

// ── Position chip ─────────────────────────────────────────────────────────────
const PositionChip = ({ value, onChange }) => (
  <div className="tw-inline-flex tw-items-center tw-gap-1 tw-h-8 tw-px-2.5 tw-text-xs tw-font-semibold tw-text-slate-700 tw-rounded-xl tw-border tw-border-slate-300 tw-bg-slate-50 hover:tw-bg-slate-100 hover:tw-border-slate-400 tw-transition focus-within:tw-border-primary-500 focus-within:tw-bg-white focus-within:tw-ring-2 focus-within:tw-ring-primary-500/15">
    <FiHash size={11} className="tw-text-slate-500" />
    <input
      type="number"
      value={value}
      onChange={e => onChange(parseInt(e.target.value, 10) || 0)}
      className="tw-w-9 tw-bg-transparent tw-border-0 tw-outline-none tw-text-center tw-text-xs tw-font-bold tw-text-slate-700"
      title="Position (order)"
    />
  </div>
);

PositionChip.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
};

// ── Publish toggle chip — real ToggleSwitch wrapped with label ────────────────
const PublishChip = ({ value, onChange }) => (
  <div
    className={`tw-inline-flex tw-items-center tw-gap-2 tw-h-8 tw-px-3 tw-text-[11px] tw-font-bold tw-rounded-xl tw-border tw-uppercase tw-tracking-wider tw-transition-all ${
      value
        ? 'tw-bg-emerald-50 tw-border-emerald-200 tw-text-emerald-700'
        : 'tw-bg-amber-50 tw-border-amber-200 tw-text-amber-700'
    }`}
    title="Toggle published / draft"
  >
    {value ? <FiEye size={11} /> : <FiEyeOff size={11} />}
    <span>{value ? 'Published' : 'Draft'}</span>
    <ToggleSwitch size="sm" value={value} onChange={onChange} />
  </div>
);

PublishChip.propTypes = {
  value: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

// ── Guide form drawer ─────────────────────────────────────────────────────────
const GuideForm = ({ guide, show, onClose, onSaved, onDelete, existingGroups = [] }) => {
  const dispatch = useDispatch();
  const saving   = useSelector(s => s.user_guide.saving);
  const isEdit   = !!guide;

  const [form, setForm]     = useState(EMPTY_FORM);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (!show) return;
    setForm(guide ? {
      title:        guide.title        || '',
      group_name:   guide.group_name   || 'General',
      description:  guide.description  || '',
      content_html: guide.content_html || '',
      position:     guide.position     ?? 0,
      published:    guide.published    ?? false,
    } : EMPTY_FORM);
    setErrors([]);
  }, [show, guide]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const canSubmit = form.title.trim().length > 0 && !saving;

  const handleSubmit = async (overrides = {}) => {
    setErrors([]);
    const payload = { ...form, ...overrides };
    const result = await dispatch(
      isEdit
        ? updateUserGuide({ slug: guide.slug, payload })
        : createUserGuide(payload)
    );
    if (result.error) {
      setErrors(result.payload?.errors || ['An error occurred. Please try again.']);
    } else {
      onSaved(result.payload);
    }
  };

  // Keyboard shortcuts: Cmd/Ctrl+S → save, Esc → cancel
  useEffect(() => {
    if (!show) return undefined;
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (canSubmit) handleSubmit();
      } else if (e.key === 'Escape' && !saving) {
        const tag = (e.target.tagName || '').toLowerCase();
        const isText = tag === 'input' || tag === 'textarea' || e.target.isContentEditable;
        if (!isText) {
          e.preventDefault();
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [show, canSubmit, saving, onClose]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = () => {
    if (!guide || !onDelete) return;
    onDelete(guide);
  };

  const footer = (
    <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-gap-3 tw-flex-wrap">
      {/* Left side */}
      <div className="tw-flex tw-items-center tw-gap-3">
        {isEdit && onDelete && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={saving}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-text-xs tw-font-semibold tw-text-rose-600 hover:tw-text-rose-700 hover:tw-bg-rose-50 tw-bg-transparent tw-border tw-border-rose-200 hover:tw-border-rose-300 tw-rounded tw-transition-all tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            <FiTrash2 size={13} /> Delete
          </button>
        )}
      </div>

      {/* Right side */}
      <div className="tw-flex tw-items-center tw-gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          title="Cancel (Esc)"
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3.5 tw-text-xs tw-font-semibold tw-text-slate-700 hover:tw-text-slate-900 hover:tw-bg-slate-100 tw-bg-transparent tw-border tw-border-slate-300 hover:tw-border-slate-400 tw-rounded tw-transition-all tw-cursor-pointer disabled:tw-opacity-50"
        >
          <FiX size={13} /> Cancel
        </button>

        {!form.published && form.title.trim() && (
          <button
            type="button"
            onClick={() => handleSubmit({ published: true })}
            disabled={!canSubmit}
            title="Save and publish immediately"
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3.5 tw-text-xs tw-font-semibold tw-text-emerald-700 hover:tw-text-emerald-800 tw-bg-emerald-50 hover:tw-bg-emerald-100 tw-border tw-border-emerald-200 hover:tw-border-emerald-300 tw-rounded tw-transition-all tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            <FiEye size={13} /> Save &amp; Publish
          </button>
        )}

        <button
          type="button"
          onClick={() => handleSubmit()}
          disabled={!canSubmit}
          title={isEdit ? 'Save changes (Ctrl S)' : 'Create guide (Ctrl S)'}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-4 tw-text-xs tw-font-bold tw-text-white tw-bg-gradient-to-r tw-from-primary-600 tw-to-blue-600 hover:tw-from-primary-700 hover:tw-to-blue-700 disabled:tw-from-slate-400 disabled:tw-to-slate-500 tw-border-0 tw-rounded tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-cursor-pointer disabled:tw-opacity-70 disabled:tw-cursor-not-allowed"
        >
          {saving ? (
            <>
              <span className="tw-inline-block tw-h-3 tw-w-3 tw-border-2 tw-border-white/30 tw-border-t-white tw-rounded-xl tw-animate-spin" />
              Saving...
            </>
          ) : (
            <>
              {isEdit ? <FiSave size={13} /> : <FiPlus size={13} />}
              {isEdit ? 'Save Changes' : 'Create Guide'}
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <Drawer
      show={show}
      onHide={onClose}
      title={isEdit ? 'Edit Guide' : 'New Guide'}
      footer={footer}
      width="960px"
      minWidth="560px"
    >
      <div className="tw-space-y-5">
        {/* Errors */}
        {errors.length > 0 && (
          <div className="tw-flex tw-items-start tw-gap-2 tw-p-3 tw-bg-rose-50 tw-border tw-border-rose-200 tw-rounded-lg tw-text-sm tw-text-rose-700">
            <FiAlertCircle className="tw-shrink-0 tw-mt-0.5" size={15} />
            <ul className="tw-list-disc tw-pl-3 tw-space-y-0.5 tw-mb-0">
              {errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}

        {/* Meta chip row */}
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <GroupCombobox
            value={form.group_name}
            onChange={v => set('group_name', v)}
            suggestions={existingGroups}
          />
          <PositionChip
            value={form.position}
            onChange={v => set('position', v)}
          />
          <PublishChip
            value={form.published}
            onChange={v => set('published', v)}
          />
        </div>

        {/* Title — h1 style */}
        <input
          type="text"
          value={form.title}
          onChange={e => set('title', e.target.value)}
          placeholder="Untitled guide"
          className="tw-w-full tw-text-3xl tw-font-bold tw-text-slate-900 tw-tracking-tight tw-bg-transparent tw-border-0 tw-outline-none placeholder:tw-text-slate-300 tw-px-0 tw-py-1 focus:tw-outline-none"
        />

        {/* Description — subtitle style */}
        <input
          type="text"
          value={form.description}
          onChange={e => set('description', e.target.value)}
          placeholder="Add a short summary..."
          className="tw-w-full tw-text-base tw-text-slate-600 tw-bg-transparent tw-border-0 tw-outline-none placeholder:tw-text-slate-400 tw-px-0 tw-py-1 focus:tw-outline-none"
        />

        {/* Divider */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-text-[10px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-pt-1">
          <span className="tw-flex-1 tw-h-px tw-bg-slate-200" />
          <span>Content</span>
          <span className="tw-flex-1 tw-h-px tw-bg-slate-200" />
        </div>

        {/* Rich editor */}
        <RichTextEditor
          value={form.content_html}
          onChange={v => set('content_html', v)}
          minHeight={420}
          maxHeight={640}
          placeholder="Start writing your guide..."
          onImageUpload={uploadUserGuideImage}
        />
      </div>
    </Drawer>
  );
};

GuideForm.propTypes = {
  guide: PropTypes.shape({
    slug: PropTypes.string,
    title: PropTypes.string,
    group_name: PropTypes.string,
    description: PropTypes.string,
    content_html: PropTypes.string,
    position: PropTypes.number,
    published: PropTypes.bool,
  }),
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  existingGroups: PropTypes.arrayOf(PropTypes.string),
};

// ── Guide row ─────────────────────────────────────────────────────────────────
const GuideRow = ({ guide, color, onSelect, onEdit, onDelete, onTogglePublish }) => (
  <div className="tw-group tw-flex tw-items-center tw-gap-3 tw-pl-5 tw-pr-6 tw-py-3 hover:tw-bg-slate-100/60 tw-border-b tw-border-slate-100 last:tw-border-0 tw-transition-colors">
    <div className={`tw-w-1 tw-h-9 tw-rounded-xl ${color.dot} tw-opacity-70`} />
    <div className="tw-flex-1 tw-min-w-0 tw-cursor-pointer" onClick={() => onSelect(guide)}>
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-0.5">
        <FiFileText size={12} className="tw-text-slate-400 tw-shrink-0" />
        <span className="tw-text-sm tw-font-semibold tw-text-slate-800 tw-truncate">
          {stripEmoji(guide.title)}
        </span>
        {!guide.published && (
          <span className="tw-text-[9px] tw-font-bold tw-text-amber-800 tw-bg-amber-100 tw-border tw-border-amber-200 tw-px-1.5 tw-py-0.5 tw-rounded-sm tw-uppercase">
            Draft
          </span>
        )}
        <span className="tw-text-[9px] tw-font-bold tw-text-slate-500 tw-bg-slate-200/70 tw-px-1.5 tw-py-0.5 tw-rounded-sm tw-flex tw-items-center tw-gap-0.5">
          <FiHash size={9} />{guide.position ?? 0}
        </span>
      </div>
      {guide.description && (
        <p className="tw-text-xs tw-text-slate-500 tw-truncate tw-mb-0 tw-pl-5">{guide.description}</p>
      )}
    </div>
    <div className="tw-flex tw-items-center tw-gap-3 tw-shrink-0 tw-pl-3">
      <div className="tw-flex tw-items-center tw-h-7" title={guide.published ? 'Published — toggle to unpublish' : 'Draft — toggle to publish'}>
        <ToggleSwitch
          size="md"
          value={guide.published}
          onChange={v => onTogglePublish(guide, v)}
        />
      </div>
      <div className="tw-flex tw-items-center tw-gap-0.5 tw-pl-3 tw-border-l tw-border-slate-200">
        <button
          type="button"
          title="Edit guide"
          onClick={() => onEdit(guide)}
          className="tw-h-7 tw-w-7 tw-flex tw-items-center tw-justify-center tw-rounded-md tw-text-slate-500 hover:tw-text-primary-700 hover:tw-bg-primary-100/60 tw-bg-transparent tw-border-0 tw-transition-colors tw-cursor-pointer"
        >
          <FiEdit2 size={14} strokeWidth={1.75} />
        </button>
        <button
          type="button"
          title="Delete guide"
          onClick={() => onDelete(guide)}
          className="tw-h-7 tw-w-7 tw-flex tw-items-center tw-justify-center tw-rounded-md tw-text-slate-500 hover:tw-text-rose-700 hover:tw-bg-rose-100/60 tw-bg-transparent tw-border-0 tw-transition-colors tw-cursor-pointer"
        >
          <FiTrash2 size={14} strokeWidth={1.75} />
        </button>
      </div>
    </div>
  </div>
);

GuideRow.propTypes = {
  guide: PropTypes.shape({
    slug: PropTypes.string,
    title: PropTypes.string,
    group_name: PropTypes.string,
    description: PropTypes.string,
    content_html: PropTypes.string,
    position: PropTypes.number,
    published: PropTypes.bool,
  }).isRequired,
  color: PropTypes.shape({
    dot: PropTypes.string,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onTogglePublish: PropTypes.func.isRequired,
};

// ── Stat card (also acts as a filter) ─────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, iconBg, iconText, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-shadow-[0_4px_20px_-8px_rgba(15,23,42,0.08)] tw-px-4 tw-py-3 tw-text-left tw-cursor-pointer tw-transition-all hover:-tw-translate-y-0.5 ${
      active
        ? 'tw-border-primary-400 tw-bg-primary-50/70'
        : 'tw-border-slate-200/80 tw-bg-slate-50 hover:tw-border-slate-300'
    }`}
  >
    <div className={`tw-p-2.5 tw-rounded-lg ${iconBg}`}>
      <Icon className={iconText} size={18} />
    </div>
    <div>
      <p className="tw-text-[10px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-mb-0.5">{label}</p>
      <p className="tw-text-2xl tw-font-bold tw-text-slate-900 tw-mb-0 tw-leading-none">{value}</p>
    </div>
  </button>
);

StatCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.node,
  value: PropTypes.node,
  iconBg: PropTypes.string,
  iconText: PropTypes.string,
  active: PropTypes.bool,
  onClick: PropTypes.func,
};

// ── Admin panel ───────────────────────────────────────────────────────────────
const UserGuideAdminPanel = ({ onGuideSelect, onGuideUpdated }) => {
  const dispatch = useDispatch();
  const { data: guides, loading } = useSelector(s => s.user_guide);
  const [showForm, setShowForm]   = useState(false);
  const [editGuide, setEditGuide] = useState(null);
  const [search, setSearch]       = useState('');
  const [filter, setFilter]       = useState('all'); // all | published | draft

  useEffect(() => { dispatch(fetchUserGuides()); }, [dispatch]);

  const openCreate = () => { setEditGuide(null); setShowForm(true); };
  const openEdit   = g  => { setEditGuide(g);    setShowForm(true); };
  const closeForm  = () => { setShowForm(false); setEditGuide(null); };

  const handleDelete = (guide, { closeFormAfter = false } = {}) => {
    confirmAlert({
      title:   `Delete "${stripEmoji(guide.title)}"`,
      message: 'This action cannot be undone.',
      type:    'danger',
      onConfirm: async () => {
        await dispatch(deleteUserGuide(guide.slug));
        dispatch(fetchUserGuides());
        if (closeFormAfter) closeForm();
      },
    });
  };

  const handleDeleteFromForm = (g) => handleDelete(g, { closeFormAfter: true });

  const handleSaved = (saved) => {
    closeForm();
    dispatch(fetchUserGuides());
    if (saved) onGuideUpdated?.(saved);
  };

  const handleTogglePublish = (guide, v) => {
    dispatch(updateUserGuide({ slug: guide.slug, payload: { published: v } }));
  };

  // Filter + search
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return guides.filter(g => {
      if (filter === 'published' && !g.published) return false;
      if (filter === 'draft' && g.published) return false;
      if (!q) return true;
      return stripEmoji(g.title).toLowerCase().includes(q)
        || (g.description || '').toLowerCase().includes(q)
        || (g.group_name || '').toLowerCase().includes(q);
    });
  }, [guides, search, filter]);

  const grouped = useMemo(() =>
    filtered.reduce((acc, g) => {
      const { parent, sub } = parseGroup(g.group_name);
      if (!acc[parent]) acc[parent] = {};
      if (!acc[parent][sub]) acc[parent][sub] = [];
      acc[parent][sub].push(g);
      return acc;
    }, {}),
  [filtered]);

  // Build dynamic group suggestions from existing data (sorted)
  const existingGroups = useMemo(
    () => Array.from(new Set(guides.map(g => g.group_name).filter(Boolean))).sort(),
    [guides],
  );

  const total     = guides.length;
  const published = guides.filter(g => g.published).length;
  const drafts    = total - published;

  return (
    <>
      {/* Stats header — also act as filters */}
      <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3 tw-mb-4">
        <StatCard
          icon={FiFileText} label="Total guides" value={total}
          iconBg="tw-bg-primary-100" iconText="tw-text-primary-700"
          active={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        <StatCard
          icon={FiEye} label="Published" value={published}
          iconBg="tw-bg-emerald-100" iconText="tw-text-emerald-700"
          active={filter === 'published'}
          onClick={() => setFilter('published')}
        />
        <StatCard
          icon={FiEyeOff} label="Drafts" value={drafts}
          iconBg="tw-bg-amber-100" iconText="tw-text-amber-700"
          active={filter === 'draft'}
          onClick={() => setFilter('draft')}
        />
      </div>

      {/* Main panel */}
      <div className="tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-slate-50 tw-shadow-[0_4px_20px_-8px_rgba(15,23,42,0.08)] tw-overflow-hidden">
        {/* Header bar */}
        <div className="tw-px-5 tw-py-4 tw-border-b tw-border-slate-200/70 tw-bg-gradient-to-r tw-from-primary-50/70 tw-to-sky-50/40 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-2">
            <div className="tw-p-1.5 tw-bg-primary-100 tw-rounded">
              <FiFolder className="tw-text-primary-700" size={14} />
            </div>
            <div>
              <p className="tw-text-sm tw-font-bold tw-text-slate-900 tw-mb-0 tw-leading-tight">Manage Guides</p>
              <p className="tw-text-xs tw-text-slate-500 tw-mb-0">Create, edit and organise all guides.</p>
            </div>
          </div>

          <div className="tw-flex-1" />

          {/* Search */}
          <div className="tw-relative">
            <FiSearch className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400 tw-pointer-events-none" size={13} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Filter guides..."
              className="tw-w-52 tw-pl-9 tw-pr-8 tw-h-9 tw-text-sm tw-text-slate-800 placeholder:tw-text-slate-400 tw-border tw-border-slate-300 tw-rounded-xl tw-bg-white hover:tw-border-slate-400 focus:tw-border-primary-500 focus:tw-ring-4 focus:tw-ring-primary-500/15 tw-outline-none tw-transition"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="tw-absolute tw-right-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400 hover:tw-text-rose-600 tw-border-0 tw-bg-transparent tw-cursor-pointer"
                aria-label="Clear filter"
              >
                <FaTimesCircle size={13} />
              </button>
            )}
          </div>

          <Button size="sm" variant="primary" onClick={openCreate}>
            <FiPlus size={13} /> New Guide
          </Button>
        </div>

        {/* Active filter bar (when filter !== all) */}
        {filter !== 'all' && (
          <div className="tw-px-5 tw-py-2 tw-bg-primary-50/40 tw-border-b tw-border-primary-100 tw-flex tw-items-center tw-gap-2 tw-text-xs">
            <span className="tw-text-slate-600">Showing only:</span>
            <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-font-bold tw-px-2 tw-py-0.5 tw-rounded-sm ${
              filter === 'published' ? 'tw-bg-emerald-100 tw-text-emerald-700' : 'tw-bg-amber-100 tw-text-amber-700'
            }`}>
              {filter === 'published' ? <FiEye size={11} /> : <FiEyeOff size={11} />}
              {filter === 'published' ? 'Published' : 'Drafts'}
            </span>
            <button
              type="button"
              onClick={() => setFilter('all')}
              className="tw-ml-auto tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-primary-700 hover:tw-text-primary-900 tw-font-semibold tw-border-0 tw-bg-transparent tw-cursor-pointer"
            >
              <FiX size={12} /> Clear filter
            </button>
          </div>
        )}

        {/* List */}
        <div className="auto-hide-scroll tw-max-h-[64vh] tw-overflow-y-auto">
          {loading ? (
            <p className="tw-text-sm tw-text-slate-500 tw-text-center tw-py-12 tw-mb-0">Loading…</p>
          ) : filtered.length === 0 ? (
            <div className="tw-text-center tw-py-16 tw-px-6">
              <div className="tw-inline-flex tw-p-4 tw-rounded-xl tw-bg-slate-100 tw-mb-3">
                <FiFileText size={28} className="tw-text-slate-400" />
              </div>
              <p className="tw-text-sm tw-font-semibold tw-text-slate-700 tw-mb-1">
                {search || filter !== 'all' ? 'No matching guides' : 'No guides yet'}
              </p>
              <p className="tw-text-xs tw-text-slate-500 tw-mb-3">
                {search || filter !== 'all'
                  ? 'Try adjusting the filter or search term.'
                  : 'Get started by creating your first user guide.'}
              </p>
              {!search && filter === 'all' && (
                <Button size="sm" variant="primary" onClick={openCreate}>
                  <FiPlus size={13} /> Create your first guide
                </Button>
              )}
            </div>
          ) : Object.entries(grouped).map(([parent, subGroups]) => {
            const c = colorForGroup(parent);
            const groupCount = Object.values(subGroups).reduce((n, arr) => n + arr.length, 0);
            return (
              <div key={parent}>
                <div className={`tw-px-5 tw-py-2.5 tw-border-b tw-border-slate-200/70 tw-flex tw-items-center tw-gap-2 ${c.tint}`}>
                  <span className={`tw-w-2 tw-h-2 tw-rounded-xl ${c.dot}`} />
                  <p className={`tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-mb-0 ${c.text}`}>
                    {stripEmoji(parent)}
                  </p>
                  <span className={`tw-ml-auto tw-text-[10px] tw-font-bold tw-px-1.5 tw-rounded-sm ${c.countBg}`}>
                    {groupCount}
                  </span>
                </div>
                {Object.entries(subGroups).map(([sub, items]) => (
                  <div key={sub || '__top__'}>
                    {sub && (
                      <div className="tw-px-5 tw-py-1.5 tw-bg-slate-50 tw-border-b tw-border-slate-100">
                        <p className="tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-mb-0">
                          <FiChevronRight size={9} />{stripEmoji(sub)}
                        </p>
                      </div>
                    )}
                    {items.map(guide => (
                      <GuideRow
                        key={guide.id}
                        guide={guide}
                        color={c}
                        onSelect={onGuideSelect}
                        onEdit={openEdit}
                        onDelete={handleDelete}
                        onTogglePublish={handleTogglePublish}
                      />
                    ))}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      <GuideForm
        key={showForm ? `open-${editGuide?.id ?? 'new'}` : 'closed'}
        show={showForm}
        guide={editGuide}
        onClose={closeForm}
        onSaved={handleSaved}
        onDelete={handleDeleteFromForm}
        existingGroups={existingGroups}
      />
    </>
  );
};

UserGuideAdminPanel.propTypes = {
  onGuideSelect: PropTypes.func.isRequired,
  onGuideUpdated: PropTypes.func,
};

export default UserGuideAdminPanel;
